import React, { useState, useEffect, useRef, useMemo } from "react";
import { trackError, trackTimeLost } from "../../../../lib/metrics";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { ModalRegister, Modal } from "../../../../components/ui/ModalRegister";
import { SymbolShiftPuzzle } from "../../../origami/components/puzzles/SymbolShiftPuzzle";
import { PC_PASSWORD, FOLDER_PASSWORD } from "../../../../data/passwords";
import { NOTEBOOK_PAGES } from "../../../../data/origami/notebook_pages";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import NotebookViewer from "../../../origami/components/puzzles/NotebookViewer";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import IncidentOrderPuzzle from "../../../origami/components/puzzles/IncidentOrderPuzzle";
import PostitOrderMiniPuzzle from "../../../origami/components/puzzles/PostitOrderMiniPuzzle";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { getInspectKeys } from "../../../../data/inspectables";
import HintsModalHub from "../../../../components/ui/HintsModalHub";
import { useScenarioTracker } from "../../../../hooks/useScenarioTracker";
import OrigamiCommunicationModal from "../../../origami/components/ui/OrigamiCommunicationModal";
import { useOrigamiReboot } from "../../../../hooks/useOrigamiReboot";
import { rebootText } from "../../../../data/origami/origamiTexts";

// — Helpers locaux (identiques dans toutes les variantes) —
const renderDots = (onIds) => {
  const on = new Set(onIds);
  return (
    <div className="grid grid-cols-3 gap-1">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((id) => (
        <div
          key={id}
          className={["w-5 h-5 rounded-full", on.has(id) ? "bg-amber-200" : "bg-slate-600/40"].join(" ")}
        />
      ))}
    </div>
  );
};

const SHAPES = [
  { ids: [2, 3, 7], label: "①" },
  { ids: [2, 5, 9], label: "②" },
  { ids: [2, 3, 5, 7, 9], label: "③" },
  { ids: [2, 3, 5], label: "④" },
  { ids: [3, 5, 7, 8], label: "⑤" },
  { ids: [2, 3, 5, 7, 8], label: "⑥" },
  { ids: [1, 2, 3, 5, 7], label: "⑦" },
  { ids: [3, 5, 7, 8, 9], label: "⑧" },
];

/**
 * Stage 1 — Joueur 3 — Composant partagé (4J / 5J / 6J)
 *
 * Props spécifiques à la variante :
 *   playerCount    {number}  — 4, 5 ou 6 → getInspectKeys
 *   registerOwner  {string}  — "p2" (4J/5J) ou "p6" (6J)
 *                              Détermine les clés missionResolve après déverrouillage PC :
 *                              s1.{registerOwner}.rhRegister.found / .employeeId.found
 */
export default function Stage1Player3Room({
  code,
  playerId,
  canAdvance,
  hintSignal,
  raisedCount,
  raisedPlayers,
  centerMaxH,
  pseudo,
  playerCount,
  registerOwner, // "p2" | "p6"
}) {
  const INSPECT_KEYS = useMemo(() => {
    return getInspectKeys({ gameId: 1, playerCount, stage: 1, seat: 3 });
  }, [playerCount]);

  // — Modales —
  const [showPcModal, setShowPcModal] = useState(false);
  const [showNotebookModal, setShowNotebookModal] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [showPuzzleModal, setShowPuzzleModal] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showShapesModal, setShowShapesModal] = useState(false);
  const [showPostitOrderModal, setShowPostitOrderModal] = useState(false);

  // — PC —
  const [pcPassword, setPcPassword] = useState("");
  const [pcError, setPcError] = useState("");
  const [isPcUnlocked, setIsPcUnlocked] = useState(false);

  // — Explorateur —
  const [currentFolder, setCurrentFolder] = useState("root");
  const [openFile, setOpenFile] = useState(null);
  const [askingIncidentPwd, setAskingIncidentPwd] = useState(false);
  const [folderPassword, setFolderPassword] = useState("");
  const [folderError, setFolderError] = useState("");
  const [folderUnlocked, setFolderUnlocked] = useState(false);
  const [hasTriggeredAdvance, setHasTriggeredAdvance] = useState(false);

  // — Puzzle bois —
  const puzzleStartedAtRef = useRef(null);
  const puzzleLastAtRef = useRef(null);
  const puzzleSentRef = useRef(false);
  const [puzzleElapsedMs, setPuzzleElapsedMs] = useState(null);

  // — Téléphone —
  const [phoneUnlocked, setPhoneUnlocked] = useState(false);

  // — Post-it order —
  const [postitOrderSolved, setPostitOrderSolved] = useState(false);

  // — BG —
  const [bgReady, setBgReady] = useState(false);

  // — Hooks —
  const { inspectedCount, total } = useInspectProgress({
    sessionCode: code, stage: 1, playerId: Number(playerId), keys: INSPECT_KEYS,
  });
  const { showOrigamiReboot, setShowOrigamiReboot } = useOrigamiReboot({ code, playerId });
  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 1 });
  const { unlockScenarioStep } = useScenarioTracker({ code, stage: 1 });

  const usedStorageKey = useMemo(() => `hints_used:${code}:s1:p${playerId}`, [code, playerId]);

  // — Post-it draft (sessionStorage) —
  const postitDraftKey = useMemo(() => `s1:p3:postitDraft:${code}`, [code]);
  const [postitDraft, setPostitDraft] = useState(() => {
    try {
      const raw = sessionStorage.getItem(postitDraftKey);
      return raw ? JSON.parse(raw) : { seq: [], tries: [], feedback: "" };
    } catch { return { seq: [], tries: [], feedback: "" }; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(postitDraftKey, JSON.stringify(postitDraft)); } catch { }
  }, [postitDraft, postitDraftKey]);

  // — Effects —
  useEffect(() => { setBgReady(false); }, []);
  useEffect(() => { if (!hintSignal) return; setShowHintModal(true); }, [hintSignal]);

  // Envoi timeLost au démontage si puzzle commencé mais pas résolu
  useEffect(() => {
    return () => {
      if (puzzleStartedAtRef.current && !puzzleSentRef.current) {
        const end = puzzleLastAtRef.current ?? Date.now();
        const ms = end - puzzleStartedAtRef.current;
        if (ms > 1000) sendTimeLostMs(ms);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // — Helpers —
  const sendTimeLostMs = (ms) => {
    if (puzzleSentRef.current || ms <= 0) return;
    puzzleSentRef.current = true;
    trackTimeLost({ sessionCode: code, stage: 1, playerId, seconds: Math.floor(ms / 1000) });
  };

  const resetExplorer = () => {
    setCurrentFolder("root");
    setOpenFile(null);
    setAskingIncidentPwd(false);
    setFolderPassword("");
    setFolderError("");
  };

  // — Handlers PC —
  const handlePcSubmit = (e) => {
    e.preventDefault();
    if (pcPassword.trim().toUpperCase() === PC_PASSWORD) {
      setIsPcUnlocked(true);
      setPcError("");
      resetExplorer();
      missionResolve({ key: "s1.p2.marie.found" });
      // clés qui varient selon la variante (registre chez p2 en 4J/5J, chez p6 en 6J)
      missionResolve({ key: `s1.${registerOwner}.rhRegister.found` });
      missionResolve({ key: `s1.${registerOwner}.employeeId.found` });
      missionResolve({ key: "s1.p3.pc.found" });
      missionUnlock({ key: "s1.p3.pc.opened", text: `${pseudo} a déverrouillé le PC de Marie.` });
    } else {
      trackError({ sessionCode: code, stage: 1, playerId, reason: "wrong_pc_password" });
      setPcError("Mot de passe incorrect.");
    }
  };

  const handleIncidentSubmit = (e) => {
    e.preventDefault();
    if (folderPassword.trim().toUpperCase() === FOLDER_PASSWORD) {
      setFolderUnlocked(true);
      setFolderError("");
      setOpenFile("incidents");
      setAskingIncidentPwd(false);
      missionResolve({ key: "s1.p3.morseCode.found" });
      missionResolve({ key: "s1.p3.incidentlogs.found" });
      unlockScenarioStep(["s2.logs.opened", "s2.miragio.mentioned"]);
      if (!hasTriggeredAdvance && typeof canAdvance === "function") {
        setHasTriggeredAdvance(true);
        canAdvance();
      }
    } else {
      trackError({ sessionCode: code, stage: 1, playerId, reason: "wrong_incidents_password" });
      setFolderError("Ce n'est pas le bon mot de passe du dossier.");
    }
  };

  // — Assets —
  const backgroundUrl = "/assets/s1-p3-room.webp";
  const pcImg = "/assets/p3-pc.webp";
  const notebookImg = "/assets/p3_notebook.webp";
  const docsImg = "/assets/p3_docs.webp";
  const puzzleImg = "/assets/p3_puzzle.webp";
  const phoneImg = "/assets/p3-phone.webp";
  const shapesImg = "/assets/p3-shapes.webp";
  const postitOrderImg = "/assets/p3-postit-order.webp";

  // — Hints (identiques toutes variantes) —
  const hintCategories = useMemo(() => [
    {
      id: "pc_and_logs", title: "Accès PC", subtitle: "Déverrouiller la session",
      hintKeyPrefix: "s1:p3:pc",
      levels: {
        l1: (<><p className="mb-2">Tu n&apos;as aucune chance de deviner ce mot de passe au hasard.</p><p className="text-xs text-gray-300">Le PC appartient à l&apos;utilisateur 359 (USER-ID 359). Il faut d&apos;abord identifier de qui il s&apos;agit.</p></>),
        l2: (<><p className="mb-2">Le registre des employés peut te dire qui est l&apos;utilisateur 359.</p><p className="text-xs text-gray-300">Une fois que tu sais qui c&apos;est, pense à ses habitudes : les mots de passe "persos" reviennent souvent.</p></>),
        l3: (<><p className="mb-2">Le PC est celui de Marie Dupont.</p><p className="text-xs text-gray-300">Elle parle tout le temps de son chat… et ce genre d&apos;info finit souvent en mot de passe. Une fois connectés, fouille surtout les <span className="font-semibold">Logs</span>.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (session + où chercher)</p><ul className="mt-2 text-xs text-gray-200 list-disc pl-5 space-y-1"><li>Code de session : <span className="font-bold text-amber-200">Nala</span></li><li>Ensuite : dossier <span className="font-semibold">Logs</span> → fichier <span className="font-mono">logs_incidents.sec</span></li></ul></>),
      },
    },
    {
      id: "morse", title: "Déchiffrage & Incidents", subtitle: "Accès aux logs",
      hintKeyPrefix: "s1:p3:incidents",
      levels: {
        l1: (<><p className="mb-2">Le fichier d&apos;incidents est verrouillé. Ce n&apos;est pas un mot de passe "classique".</p><p className="text-xs text-gray-300">Origami semble aimer les messages codés. Ce mot de passe vient d&apos;un signal à décrypter.</p></>),
        l2: (<><p className="mb-2">Pour traduire du morse, il te faut : le signal + un alphabet.</p><p className="text-xs text-gray-300">Les serveurs peuvent donner le signal, et le dictionnaire de Samir aide à le traduire.</p></>),
        l3: (<><p className="mb-2">Une fois traduit, le morse donne un mot unique (pas une phrase).</p><p className="text-xs text-gray-300">Tu peux utiliser le MorseValidator pour tester si le mot trouvé est valide.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (mot de passe)</p><p className="mt-2 text-xs text-gray-200">Mot de passe : <span className="font-bold text-amber-200">Miragio</span></p></>),
      },
    },
    {
      id: "phone", title: "Téléphone verrouillé", subtitle: "Schéma à reproduire",
      hintKeyPrefix: "s1:p3:phone",
      levels: {
        l1: (<><p className="mb-2">Ce téléphone ne se déverrouille pas avec un code… mais avec un schéma.</p><p className="text-xs text-gray-300">Le schéma n&apos;est pas à inventer : il correspond à un <span className="italic">ordre</span> (une suite logique).</p></>),
        l2: (<><p>Une feuille de papier sur le sol pourrait t'aider dans ta recherche.</p></>),
        l3: (<><p>Il y a une logique entre les deux premières cases permettant d'obtenir la troisième.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (téléphone)</p><p className="mt-2 text-xs text-gray-200">Le tracé est la somme des points allumés des cases 7 et 8. Il s'agit donc des cercles 1-2-3-5-7-8-9.</p></>),
      },
    },
    {
      id: "postits", title: "Ordre des post-its", subtitle: "Retrouver la séquence correcte",
      hintKeyPrefix: "s1:p3:postits",
      levels: {
        l1: (<><p className="mb-2">La note parle d&apos;un <span className="italic">ordre</span>. Ce n&apos;est pas au hasard.</p><p className="text-xs text-gray-300">Peut être qu'un autre joueur a besoin de l'ordre correct de ces post-its.</p></>),
        l2: (<><p className="mb-2">Il s'agit d'un mastermind.</p><p className="text-xs text-gray-300">Un rond vert correspond à un post-it de la bonne couleur et bien placé. Un rond jaune correspond à un post-it de la bonne couleur mais mal placé. Un rond rouge correspond à un post-it de la mauvaise couleur.</p></>),
        l3: (<><p className="mb-2">Fais plusieurs tentatives et par déduction tu trouveras l'ordre en t'appuyant sur les post-its bien positionnés.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (post-its)</p><p className="mt-2 text-xs text-gray-200">Le bon ordre est le suivant: "Vert-Rose-Bleu-Jaune"</p></>),
      },
    },
    {
      id: "mini_puzzle", title: "Joli Puzzle", subtitle: "Arrangement de formes",
      hintKeyPrefix: "s1:p3:puzzle",
      levels: {
        l1: (<><p>Il ne s'agit pas d'une symétrie.</p></>),
        l2: (<><p>"Prolongement"</p></>),
        l3: (<><p>Décalage & translation.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse</p><p className="mt-2 text-xs text-gray-200">Chaque ligne correspond à la ligne du dessus décalée de une case vers la gauche. L'ordre est donc :</p><p className="mt-2 text-xs text-gray-200">Cercle - Etoile - Triangle - Carré</p><p className="mt-2 text-xs text-gray-200">Etoile - Triangle - Carré - Cercle</p></>),
      },
    },
  ], []);

  // — Rendu explorateur PC (inchangé) —
  const renderExplorer = () => (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 text-xs text-gray-300">
          <span className="px-2 py-1 rounded-md bg-black/60 border border-white/10 font-mono">
            {currentFolder === "root" && "C:/UTILISATEURS/MARIE"}
            {currentFolder === "images" && "C:/UTILISATEURS/MARIE/IMAGES"}
            {currentFolder === "archives" && "C:/UTILISATEURS/MARIE/ARCHIVES"}
            {currentFolder === "logs" && "C:/UTILISATEURS/MARIE/LOGS"}
          </span>
          {currentFolder !== "root" && <span className="hidden sm:inline text-[11px] text-slate-400">Dossier ouvert</span>}
        </div>
        {currentFolder !== "root" && (
          <button type="button"
            onClick={() => { setCurrentFolder("root"); setOpenFile(null); setAskingIncidentPwd(false); }}
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-indigo-500/15 border border-indigo-400/40 text-[12px] font-semibold text-indigo-200 hover:bg-indigo-500/25 hover:border-indigo-300/60 transition"
          >
            <span className="text-sm">↩</span> Remonter au dossier parent
          </button>
        )}
      </div>

      {/* ROOT */}
      {currentFolder === "root" && (
        <div className="grid grid-cols-2 gap-3 text-xs">
          {[
            { id: "images", color: "bg-amber-500/70", label: "Images", sub: "Photos perso, fonds d'écran…" },
            { id: "archives", color: "bg-gray-500/70", label: "Archives", sub: "CV, anciens rapports…" },
            { id: "logs", color: "bg-indigo-500/70", label: "Logs", sub: "Journaux système" },
          ].map(({ id, color, label, sub }) => (
            <button key={id} type="button"
              onClick={() => { markInspectedOnce(`stage1player3:${id}`); setCurrentFolder(id); setOpenFile(null); }}
              className="rounded-lg bg-black/40 border border-white/10 p-3 text-left hover:bg-black/60"
            >
              <div className={`w-10 h-8 ${color} rounded-sm mb-1`} />
              <p className="font-semibold">{label}</p>
              <p className="text-gray-400">{sub}</p>
            </button>
          ))}
          <div className="rounded-lg bg-black/30 border border-white/5 p-3 text-left opacity-70">
            <div className="w-10 h-8 bg-slate-500/50 rounded-sm mb-1" />
            <p className="font-semibold text-gray-400">Divers</p>
            <p className="text-gray-500">Broutilles sans intérêt</p>
          </div>
        </div>
      )}

      {/* IMAGES */}
      {currentFolder === "images" && (
        <div className="space-y-2 text-xs">
          <p className="text-gray-300 mb-1">Quelques images listées :</p>
          <ul className="space-y-1 font-mono">
            {[
              { key: "cat1Image", file: "cat1", label: "IMG_2023-07-14_CHATON.jpg" },
              { key: "nebulaImage", file: "nebula", label: "WALLPAPER_NEBULA.jpg" },
              { key: "memeImage", file: "meme", label: "MEME_1.jpg" },
            ].map(({ key, file, label }) => (
              <li key={key} className="cursor-pointer hover:text-indigo-300"
                onClick={() => { markInspectedOnce(`stage1player3:${key}`); setOpenFile(file); }}>
                ▸ {label}
              </li>
            ))}
          </ul>
          {["cat1", "nebula", "meme"].map((f) => openFile === f && (
            <div key={f} className="mt-3 rounded-lg bg-black/70 border border-white/10 p-3 flex justify-center">
              <img src={`/assets/p3_img_${f === "cat1" ? "cat1" : f === "nebula" ? "nebula" : "meme1"}.webp`}
                alt="" className="w-full max-w-xs h-auto object-contain" />
            </div>
          ))}
        </div>
      )}

      {/* ARCHIVES */}
      {currentFolder === "archives" && (
        <div className="space-y-2 text-xs">
          <p className="text-gray-300 mb-1">Dossiers d'archives :</p>
          <ul className="space-y-1 font-mono">
            {[
              { key: "CV1", file: "cv1", label: "CV_2021_FINAL.pdf" },
              { key: "CV2", file: "cv2", label: "CV_2023_V6_definitif_VF.pdf" },
            ].map(({ key, file, label }) => (
              <li key={key} className="cursor-pointer hover:text-indigo-300"
                onClick={() => { markInspectedOnce(`stage1player3:${key}`); setOpenFile(file); }}>
                ▸ {label}
              </li>
            ))}
          </ul>
          {["cv1", "cv2"].map((f) => openFile === f && (
            <div key={f} className="mt-3 rounded-lg bg-black/70 border border-white/10 p-3 flex justify-center">
              <img src={`/assets/p3_img_${f.toUpperCase()}.webp`} alt="" className="w-full h-[500px] object-contain" />
            </div>
          ))}
        </div>
      )}

      {/* LOGS */}
      {currentFolder === "logs" && (
        <div className="space-y-2 text-xs">
          <p className="text-gray-300 mb-1">Fichiers de logs :</p>
          <ul className="space-y-1 font-mono">
            {[
              { key: "commitsLog", file: "commits", label: "logs_commits.txt" },
              { key: "opsNotesLog", file: "ops_notes", label: "ops_notes.txt" },
              { key: "coffeeLog", file: "coffee", label: "coffee_machine.log" },
              { key: "urgentLog", file: "urgent", label: "README_URGENT.txt" },
            ].map(({ key, file, label }) => (
              <li key={key} className="cursor-pointer hover:text-indigo-300"
                onClick={() => { markInspectedOnce(`stage1player3:${key}`); setOpenFile(file); setAskingIncidentPwd(false); }}>
                ▸ {label}
              </li>
            ))}
            <li
              className="cursor-pointer flex items-center gap-2 text-amber-300 hover:text-amber-200"
              onClick={() => {
                markInspectedOnce('stage1player3:incidentsLog');
                setOpenFile("incidents");
                setAskingIncidentPwd(true);
                missionResolve({ key: "s1.p3.pc.opened" });
                missionResolve({ key: "s1.p5.screen.found" });
                unlockScenarioStep("s1.logs.locked");
                missionUnlock({ key: "s1.p3.incidentlogs.found", text: `${pseudo} a trouvé un logs d'incidents généré par Origami, mais il est vérouillé.` });
              }}
            >
              <span className="opacity-80">▸</span><span className="text-amber-400">🔒</span><span>logs_incidents.sec</span>
            </li>
          </ul>

          {openFile === "commits" && (
            <div className="mt-2 bg-black/60 border border-white/10 rounded-lg p-3 text-[11px] font-mono text-gray-200 max-h-40 overflow-y-auto">
              <p>[21:02:11] git commit -m "refactor auth middleware"</p>
              <p>[21:37:45] git commit -m "add monitoring hook on main router"</p>
              <p>[22:01:03] git commit -m "temporary rule override (WIP)"</p>
              <p>[22:10:19] git commit -m "disable legacy safety checks"</p>
              <p className="mt-1 text-indigo-300">&gt; Certaines de ces modifications ont l'air… imprudentes.</p>
              <p className="mt-1 text-[11px] text-gray-400">(Note interne : les changements critiques devraient toujours être revus par quelqu'un d'autre et testés hors production. Sinon, on fabrique juste des incidents en série.)</p>
            </div>
          )}
          {openFile === "ops_notes" && (
            <div className="mt-2 bg-black/60 border border-white/10 rounded-lg p-3 text-[11px] font-mono text-gray-200 max-h-40 overflow-y-auto">
              <p>[09:12] Note: rappeler que "redémarrer" n'est PAS une stratégie.</p>
              <p>[11:47] Todo: documenter enfin le pare-feu. (haha)</p>
              <p>[14:03] Rappel: ne jamais tester directement en prod.</p>
              <p>[14:04] Correction: ne jamais ADMETTRE qu'on teste en prod.</p>
              <p className="mt-1 text-[11px] text-gray-400">Bonus : garder des journaux d'incidents propres et lisibles, c'est gagner du temps et éviter de gaspiller de l'énergie en cherchant dans le vide.</p>
            </div>
          )}
          {openFile === "coffee" && (
            <div className="mt-2 bg-black/60 border border-white/10 rounded-lg p-3 text-[11px] font-mono text-gray-200 max-h-40 overflow-y-auto">
              <p>[08:01] Machine ON.</p>
              <p>[08:15] Espresso #1: OK.</p>
              <p>[09:42] Espresso #2: OK.</p>
              <p>[10:17] Alerte: niveau d'eau faible.</p>
              <p>[10:18] Alerte ignorée.</p>
              <p>[10:45] INCIDENT CRITIQUE: plus de café. Productivité à 0%.</p>
              <p className="mt-1 text-amber-300">&gt; Ce log est probablement le vrai point de départ de la catastrophe.</p>
              <p className="mt-1 text-[11px] text-gray-400">PS : penser à revoir aussi la conso de capsules et de gobelets. Les incidents café, c'est drôle… les déchets plastiques un peu moins.</p>
            </div>
          )}
          {openFile === "urgent" && (
            <div className="mt-2 bg-black/60 border border-white/10 rounded-lg p-3 text-[11px] font-mono text-gray-200 max-h-40 overflow-y-auto">
              <p>SI TU LIS ÇA APRÈS 22H :</p>
              <p>- 1) Bois un verre d'eau.</p>
              <p>- 2) Va dormir.</p>
              <p>- 3) NE TOUCHE PAS AUX RÈGLES DE PARE-FEU.</p>
              <p className="mt-1 text-red-300">&gt; Sérieusement. Tu le fais toujours. Arrête.</p>
            </div>
          )}

          {openFile === "incidents" && !folderUnlocked && askingIncidentPwd && (
            <form onSubmit={handleIncidentSubmit} className="mt-3 space-y-2">
              <p className="text-[11px] text-gray-300">Ce fichier est chiffré. Un mot de passe est requis pour l'ouvrir.</p>
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-300 mb-1">Mot de passe du fichier incidents</label>
                <input type="password" value={folderPassword}
                  onChange={(e) => { setFolderPassword(e.target.value); setFolderError(""); }}
                  className="w-full px-3 py-2 rounded-lg bg-black/60 border border-indigo-300/70 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  placeholder="••••••••"
                />
              </div>
              {folderError && <p className="text-xs text-red-400">{folderError}</p>}
              <button type="submit" className="w-full py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-xs font-semibold">
                Déchiffrer logs_incidents.sec
              </button>
            </form>
          )}
          {openFile === "incidents" && folderUnlocked && (
            <p className="mt-3 text-[11px] text-emerald-300">Mot de passe accepté. Le fichier se déchiffre… (passage à l'étape suivante)</p>
          )}
        </div>
      )}
    </div>
  );

  // — Rendu —
  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div className="relative h-screen w-auto mx-auto aspect-square"
        style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>

        {/* HUD */}
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Bureau de M.D
        </div>

        {/* Fond */}
        <img src={backgroundUrl} alt="Bureau avec ordinateur verrouillé"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)} onError={() => setBgReady(true)}
        />

        {/* Hotspots */}
        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>
          <img src={postitOrderImg} alt="Post-it froissé"
            onClick={() => { markInspectedOnce("stage1player3:postit-order"); setShowPostitOrderModal(true); missionUnlock({ key: "s1.p3.postItsNote.found", text: `${pseudo} a trouvé une note de Samir parlant d'ordre de post-its.` }); }}
            className="p3-postit-order absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={phoneImg} alt="Téléphone"
            onClick={() => { markInspectedOnce("stage1player3:phone"); setShowPhoneModal(true); }}
            className="p3-phone absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={shapesImg} alt="Suite de formes"
            onClick={() => { markInspectedOnce("stage1player3:shapes"); setShowShapesModal(true); }}
            className="p3-shapes absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={pcImg} alt="Écran de l'ordinateur"
            onClick={() => { markInspectedOnce('stage1player3:pcScreen'); setShowPcModal(true); missionUnlock({ key: "s1.p3.pc.found", text: `${pseudo} a trouvé un PC vérouillé (USER-ID 359).` }); }}
            className="p3-pc absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={notebookImg} alt="Carnet de notes sur le bureau"
            onClick={() => { markInspectedOnce('stage1player3:notebook'); setShowNotebookModal(true); }}
            className="p3-notebook absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={docsImg} alt="Pile de documents administratifs"
            onClick={() => { markInspectedOnce('stage1player3:documents'); setShowDocsModal(true); }}
            className="p3-docs absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={puzzleImg} alt="puzzle"
            onClick={() => { markInspectedOnce('stage1player3:puzzle'); setShowPuzzleModal(true); }}
            className="p3-puzzle absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
        </div>
      </div>

      {/* === MODALES === */}

      {/* Post-it order */}
      <ModalRegister open={showPostitOrderModal} title="Note froissée" onClose={() => setShowPostitOrderModal(false)} onHint={() => setShowHintModal(true)}>
        <PostitOrderMiniPuzzle
          solvedProp={postitOrderSolved}
          draft={postitDraft}
          onDraftChange={setPostitDraft}
          onSolved={() => {
            setPostitOrderSolved(true);
            missionResolve({ key: "s1.p3.postItsNote.found" });
            missionUnlock({ key: "s1.p3.postItsOrder.found", text: `${pseudo} a trouvé l'ordre des post-its de Samir.` });
          }}
        />
      </ModalRegister>

      {/* Téléphone */}
      <ModalRegister
        open={showPhoneModal}
        title={phoneUnlocked ? "Téléphone — Déverrouillé" : "Téléphone — Verrouillé"}
        onClose={() => setShowPhoneModal(false)}
        onHint={() => setShowHintModal(true)}
      >
        <IncidentOrderPuzzle
          unlocked={phoneUnlocked}
          onSolved={() => setPhoneUnlocked(true)}
          pseudo={pseudo} code={code} playerId={playerId}
          onUnlockFailed={() => trackError({ sessionCode: code, stage: 1, playerId, reason: "wrong_phone_pattern" })}
        />
      </ModalRegister>

      {/* Formes */}
      <ModalRegister open={showShapesModal} title="Feuille de papier" onClose={() => setShowShapesModal(false)} onHint={() => setShowHintModal(true)}>
        <div className="space-y-3">
          <p className="text-xs text-gray-300">Une feuille avec des formes géométriques dessinées. Une des formes est manquante.</p>
          <div className="rounded-xl border border-white/10 bg-black/40 p-3">
            <p className="text-xs text-slate-300 mb-3">Trouvez la <span className="text-amber-200 font-semibold">9e case</span>.</p>
            <div className="grid grid-cols-3 gap-3">
              {SHAPES.map((s, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-2 flex flex-col items-center gap-2">
                  <div className="text-[11px] text-slate-300">{s.label}</div>
                  {renderDots(s.ids)}
                </div>
              ))}
              <div className="rounded-xl border border-amber-400/50 bg-amber-500/10 p-2 flex flex-col items-center gap-2">
                <div className="text-[11px] text-amber-200 font-semibold">⑨ ?</div>
                {renderDots([])}
              </div>
            </div>
          </div>
        </div>
      </ModalRegister>

      {/* PC */}
      <ModalRegister
        open={showPcModal}
        title={!isPcUnlocked ? "Session verrouillée" : "Bureau - Session ouverte"}
        onClose={() => { setShowPcModal(false); resetExplorer(); }}
        onHint={() => setShowHintModal(true)}
      >
        {!isPcUnlocked ? (
          <div>
            <div className="mb-4 rounded-lg bg-black/60 border border-white/10 px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">USER-ID</p>
                  <p className="font-mono text-2xl font-bold text-white mt-1">359</p>
                </div>
                <div className="px-2 py-1 rounded bg-amber-500/10 border border-amber-400/30">
                  <p className="text-[10px] font-mono text-amber-200 uppercase">Verrouillé</p>
                </div>
              </div>
            </div>
            <form onSubmit={handlePcSubmit} className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Mot de passe</label>
                <input type="password" value={pcPassword}
                  onChange={(e) => { setPcPassword(e.target.value); setPcError(""); }}
                  className="w-full px-3 py-2 rounded-lg bg-black/60 border border-white/20 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="••••••••"
                />
              </div>
              {pcError && <p className="text-xs text-red-400">{pcError}</p>}
              <button type="submit" className="mt-1 w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-sm font-semibold">
                Se connecter
              </button>
            </form>
          </div>
        ) : renderExplorer()}
      </ModalRegister>

      {/* Carnet */}
      <Modal open={showNotebookModal} title="Carnet posé sur le bureau" onClose={() => setShowNotebookModal(false)}>
        <NotebookViewer pages={NOTEBOOK_PAGES} hintStorageKey={`hint:pageflip:${code}:p3-notebook`} />
      </Modal>

      {/* Documents */}
      <Modal open={showDocsModal} title="Une pile de documents" onClose={() => setShowDocsModal(false)}>
        <p className="mb-2">En fouillant la pile, tu tombes sur une feuille imprimée intitulée <span className="italic"> "Secret Santa — idées cadeaux (brouillon)"</span>.</p>
        <p className="text-xs whitespace-pre-wrap text-gray-200 mb-2">{"• Pour Samir :\n  - bonnet ridicule pour son chien (comment il s'appelle déjà ? Balto ? Baltha... ?)\n  - demander à quelqu'un qui connaît mieux son chien\n\n• Pour Alex :\n  - mug \"logs incidents & chill\" ?\n\n• Pour l'équipe infra :\n  - café. Beaucoup de café."}</p>
        <p className="text-xs text-gray-300">Comment il s'appelle ce chien déjà...?</p>
      </Modal>

      {/* Puzzle bois */}
      <Modal open={showPuzzleModal} title="Un joli puzzle" onClose={() => setShowPuzzleModal(false)} onHint={() => setShowHintModal(true)}>
        {!puzzleElapsedMs ? (
          <>
            <p className="mb-2">Un petit puzzle en bois, posé là comme un gadget. Les symboles semblent suivre un motif… ou alors quelqu&apos;un s&apos;ennuyait très fort en réunion.</p>
            <SymbolShiftPuzzle
              onFirstInteraction={() => { puzzleStartedAtRef.current = Date.now(); }}
              onInteraction={() => { puzzleLastAtRef.current = Date.now(); }}
              onSolved={() => {
                if (!puzzleStartedAtRef.current) return;
                const elapsed = Date.now() - puzzleStartedAtRef.current;
                setPuzzleElapsedMs(elapsed);
                sendTimeLostMs(elapsed);
              }}
            />
          </>
        ) : (() => {
          const totalSeconds = Math.floor(puzzleElapsedMs / 1000);
          const minutes = Math.floor(totalSeconds / 60);
          const seconds = totalSeconds % 60;
          return (
            <p className="text-sm text-amber-300 font-semibold text-center">
              Bien joué, vous avez perdu {String(minutes).padStart(2, "0")} minutes et {String(seconds).padStart(2, "0")} secondes à résoudre un puzzle qui ne sert absolument à rien.
              <br /><span className="text-amber-200 text-xs">Je croyais que vous aviez mieux à faire que de jouer…</span>
            </p>
          );
        })()}
      </Modal>

      {/* Reboot Origami */}
      <OrigamiCommunicationModal
        open={showOrigamiReboot} text={rebootText}
        onClose={() => setShowOrigamiReboot(false)}
        autoClose={true} autoCloseDuration={10000}
      />

      {/* Indices */}
      <HintsModalHub
        open={showHintModal} onClose={() => setShowHintModal(false)}
        title="Indices — Bureau de M.D"
        markHintUsedOnce={markHintUsedOnce}
        categories={hintCategories}
        usedStorageKey={usedStorageKey}
      />
    </div>
  );
}